import React, { useState, useEffect, useMemo } from 'react';
import { getBatches, updateBatchQCStatus, getProducts } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, 
  ShieldCheck, 
  ShieldAlert, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  Package
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const QC_STATUS_CONFIG = {
  on_hold: { label: 'On Hold', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  released: { label: 'Released', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
};

export default function QCManagement() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('on_hold');
  const [productFilter, setProductFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Bulk action dialog
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('released');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkPassword, setBulkPassword] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [correctiveActionDueDate, setCorrectiveActionDueDate] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [batchesRes, productsRes] = await Promise.allSettled([
        getBatches(),
        getProducts()
      ]);
      if (batchesRes.status === 'fulfilled') setBatches(batchesRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter batches - treat null/undefined qc_status as 'on_hold'
  const filteredBatches = useMemo(() => {
    return batches.filter(batch => {
      const batchStatus = batch.qc_status || 'on_hold';
      
      // Status filter
      if (statusFilter !== 'all' && batchStatus !== statusFilter) return false;
      
      // Product filter
      if (productFilter !== 'all' && batch.product_id !== productFilter) return false;
      
      // Search
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesBatch = batch.batch_code?.toLowerCase().includes(searchLower);
        const matchesProduct = batch.product_name?.toLowerCase().includes(searchLower);
        if (!matchesBatch && !matchesProduct) return false;
      }
      
      return true;
    });
  }, [batches, statusFilter, productFilter, search]);

  // Group by product for quick view
  const groupedByProduct = useMemo(() => {
    const groups = {};
    filteredBatches.forEach(batch => {
      const key = batch.product_id || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          product_name: batch.product_name || 'Unknown',
          batches: [],
          total_quantity: 0
        };
      }
      groups[key].batches.push(batch);
      groups[key].total_quantity += batch.quantity_available || 0;
    });
    return groups;
  }, [filteredBatches]);

  // Stats - treat null/undefined as 'on_hold'
  const stats = useMemo(() => {
    const onHold = batches.filter(b => !b.qc_status || b.qc_status === 'on_hold').length;
    const released = batches.filter(b => b.qc_status === 'released').length;
    const rejected = batches.filter(b => b.qc_status === 'rejected').length;
    return { onHold, released, rejected, total: batches.length };
  }, [batches]);

  const toggleBatch = (batchId) => {
    const newSelected = new Set(selectedBatches);
    if (newSelected.has(batchId)) {
      newSelected.delete(batchId);
    } else {
      newSelected.add(batchId);
    }
    setSelectedBatches(newSelected);
  };

  const selectAll = () => {
    if (selectedBatches.size === filteredBatches.length) {
      setSelectedBatches(new Set());
    } else {
      setSelectedBatches(new Set(filteredBatches.map(b => b.id)));
    }
  };

  const selectAllOnHold = () => {
    const onHoldIds = filteredBatches
      .filter(b => !b.qc_status || b.qc_status === 'on_hold')
      .map(b => b.id);
    setSelectedBatches(new Set(onHoldIds));
  };

  const handleBulkAction = async () => {
    if (selectedBatches.size === 0) {
      toast.error('No batches selected');
      return;
    }

    if (!bulkPassword) {
      toast.error('Password is required for QC status changes (electronic signature)');
      return;
    }

    if (bulkAction === 'rejected' && !correctiveAction) {
      toast.error('Corrective action is required when rejecting batches (HACCP/CAPA requirement)');
      return;
    }

    setProcessing(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      let lastError = '';

      for (const batchId of selectedBatches) {
        try {
          await updateBatchQCStatus(batchId, {
            status: bulkAction,
            notes: bulkNotes || `Bulk ${bulkAction} via QC Management`,
            password: bulkPassword,
            corrective_action: bulkAction === 'rejected' ? correctiveAction : null,
            corrective_action_due_date: bulkAction === 'rejected' ? correctiveActionDueDate : null
          });
          successCount++;
        } catch (e) {
          errorCount++;
          lastError = e.response?.data?.detail || e.message;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} batch(es) updated to ${QC_STATUS_CONFIG[bulkAction].label}`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} batch(es) failed: ${lastError}`);
      }

      setBulkActionOpen(false);
      setSelectedBatches(new Set());
      setBulkNotes('');
      setBulkPassword('');
      setCorrectiveAction('');
      setCorrectiveActionDueDate('');
      loadData();
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setProcessing(false);
    }
  };

  // Single batch QC update with password confirmation
  const [singleQcOpen, setSingleQcOpen] = useState(false);
  const [singleQcBatch, setSingleQcBatch] = useState(null);
  const [singleQcStatus, setSingleQcStatus] = useState('released');
  const [singleQcNotes, setSingleQcNotes] = useState('');
  const [singleQcPassword, setSingleQcPassword] = useState('');
  const [singleCorrectiveAction, setSingleCorrectiveAction] = useState('');
  const [singleCorrectiveActionDueDate, setSingleCorrectiveActionDueDate] = useState('');

  const openSingleQcDialog = (batch, status = 'released') => {
    setSingleQcBatch(batch);
    setSingleQcStatus(status);
    setSingleQcNotes('');
    setSingleQcPassword('');
    setSingleCorrectiveAction('');
    setSingleCorrectiveActionDueDate('');
    setSingleQcOpen(true);
  };

  const handleSingleQcUpdate = async () => {
    if (!singleQcPassword) {
      toast.error('Password is required for QC status changes (electronic signature)');
      return;
    }

    if (singleQcStatus === 'rejected' && !singleCorrectiveAction) {
      toast.error('Corrective action is required when rejecting (HACCP/CAPA requirement)');
      return;
    }

    setProcessing(true);
    try {
      await updateBatchQCStatus(singleQcBatch.id, {
        status: singleQcStatus,
        notes: singleQcNotes,
        password: singleQcPassword,
        corrective_action: singleQcStatus === 'rejected' ? singleCorrectiveAction : null,
        corrective_action_due_date: singleQcStatus === 'rejected' ? singleCorrectiveActionDueDate : null
      });
      toast.success(`Batch ${singleQcBatch.batch_code} updated to ${QC_STATUS_CONFIG[singleQcStatus].label}`);
      setSingleQcOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update QC status');
    } finally {
      setProcessing(false);
    }
  };

  // Legacy quickRelease - now opens dialog for password confirmation
  const quickRelease = async (batch) => {
    openSingleQcDialog(batch, 'released');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qc-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            QC Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Quickly approve or reject batches for dispatch
          </p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-amber-400 transition-colors" onClick={() => setStatusFilter('on_hold')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.onHold}</p>
                <p className="text-sm text-muted-foreground">On Hold</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-400 transition-colors" onClick={() => setStatusFilter('released')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.released}</p>
                <p className="text-sm text-muted-foreground">Released</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-red-400 transition-colors" onClick={() => setStatusFilter('rejected')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Batches</p>
              </div>
              <Package className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search batches..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-48"
                  data-testid="qc-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="qc-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="released">Released</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-48" data-testid="qc-product-filter">
                  <SelectValue placeholder="Product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Actions */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground">
                {selectedBatches.size} selected
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={selectAllOnHold}
                disabled={filteredBatches.filter(b => b.qc_status === 'on_hold').length === 0}
              >
                Select All On Hold
              </Button>
              <Button
                size="sm"
                onClick={() => { setBulkAction('released'); setBulkActionOpen(true); }}
                disabled={selectedBatches.size === 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="bulk-release-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Release Selected
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => { setBulkAction('rejected'); setBulkActionOpen(true); }}
                disabled={selectedBatches.size === 0}
                data-testid="bulk-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject Selected
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Batches Table */}
      <Card>
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            {statusFilter === 'all' ? 'All Batches' : `${QC_STATUS_CONFIG[statusFilter]?.label || statusFilter} Batches`}
            <span className="text-muted-foreground font-normal ml-2">({filteredBatches.length})</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={selectAll}>
            {selectedBatches.size === filteredBatches.length ? 'Deselect All' : 'Select All'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {filteredBatches.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="w-10 px-4 py-3"></th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Batch Code</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Production Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Use By</th>
                    <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Qty</th>
                    <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Weight</th>
                    <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Cases</th>
                    <th className="text-center px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="w-32 px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Quick Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredBatches.map((batch) => {
                    const batchStatus = batch.qc_status || 'on_hold';
                    const statusConfig = QC_STATUS_CONFIG[batchStatus] || QC_STATUS_CONFIG.on_hold;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr 
                        key={batch.id} 
                        className={`hover:bg-muted/30 ${selectedBatches.has(batch.id) ? 'bg-primary/5' : ''}`}
                        data-testid={`qc-batch-${batch.id}`}
                      >
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selectedBatches.has(batch.id)}
                            onCheckedChange={() => toggleBatch(batch.id)}
                          />
                        </td>
                        <td className="px-4 py-3 font-mono text-sm font-medium">{batch.batch_code}</td>
                        <td className="px-4 py-3 text-sm">{batch.product_name || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{batch.production_date || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{batch.use_by_date || '-'}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{(batch.dispatch_qty || batch.quantity_available || 0).toFixed(0)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-blue-700">{(batch.dispatch_weight || 0).toFixed(2)} kg</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{(batch.dispatch_cases || 0).toFixed(0)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {(!batch.qc_status || batch.qc_status === 'on_hold') && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => openSingleQcDialog(batch, 'released')}
                                title="Release"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openSingleQcDialog(batch, 'rejected')}
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {batch.qc_status === 'released' && (
                            <div className="flex gap-1 items-center">
                              <span className="text-xs text-green-600 mr-1">Ready</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => openSingleQcDialog(batch, 'on_hold')}
                                title="Put On Hold"
                              >
                                <Clock className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                          {batch.qc_status === 'rejected' && (
                            <div className="flex gap-1 items-center">
                              <span className="text-xs text-red-600 mr-1">Rejected</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => openSingleQcDialog(batch, 'on_hold')}
                                title="Put On Hold"
                              >
                                <Clock className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <ShieldAlert className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No batches found</h3>
              <p className="text-sm text-muted-foreground">
                {statusFilter !== 'all' 
                  ? `No ${QC_STATUS_CONFIG[statusFilter]?.label.toLowerCase()} batches` 
                  : 'No batches match your filters'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkActionOpen} onOpenChange={setBulkActionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'released' ? (
                <><CheckCircle2 className="w-5 h-5 text-green-600" /> Release {selectedBatches.size} Batch(es)</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-600" /> Reject {selectedBatches.size} Batch(es)</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className={`p-3 rounded-lg ${bulkAction === 'released' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm ${bulkAction === 'released' ? 'text-green-700' : 'text-red-700'}`}>
                {bulkAction === 'released' 
                  ? 'These batches will be available for dispatch after release.'
                  : 'These batches will be blocked from dispatch.'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                placeholder={`Reason for bulk ${bulkAction}...`}
                rows={2}
              />
            </div>

            {/* CAPA fields for rejection - HACCP/BRC compliance */}
            {bulkAction === 'rejected' && (
              <>
                <div className="space-y-2">
                  <Label className="text-red-700">Corrective Action (Required) *</Label>
                  <Textarea
                    value={correctiveAction}
                    onChange={(e) => setCorrectiveAction(e.target.value)}
                    placeholder="Describe the corrective action to be taken..."
                    rows={2}
                    className="border-red-200"
                    data-testid="capa-action-input"
                  />
                  <p className="text-xs text-muted-foreground">HACCP/CAPA requirement: Document corrective action for rejected batches</p>
                </div>
                <div className="space-y-2">
                  <Label>Corrective Action Due Date</Label>
                  <Input
                    type="text"
                    value={correctiveActionDueDate}
                    onChange={(e) => setCorrectiveActionDueDate(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    data-testid="capa-due-date-input"
                  />
                </div>
              </>
            )}

            {/* Password confirmation for electronic signature - 21 CFR Part 11 */}
            <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-blue-700 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Electronic Signature (Required) *
              </Label>
              <Input
                type="password"
                value={bulkPassword}
                onChange={(e) => setBulkPassword(e.target.value)}
                placeholder="Enter your password to confirm"
                data-testid="qc-password-input"
              />
              <p className="text-xs text-blue-600">Your password confirms this QC decision (21 CFR Part 11 compliant)</p>
            </div>
            
            <div className="max-h-32 overflow-y-auto text-sm text-muted-foreground border rounded p-2">
              <p className="font-medium mb-1">Selected batches:</p>
              {Array.from(selectedBatches).slice(0, 10).map(id => {
                const batch = batches.find(b => b.id === id);
                return batch ? (
                  <div key={id} className="font-mono text-xs">{batch.batch_code}</div>
                ) : null;
              })}
              {selectedBatches.size > 10 && (
                <div className="text-xs">...and {selectedBatches.size - 10} more</div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAction} 
              disabled={processing || !bulkPassword || (bulkAction === 'rejected' && !correctiveAction)}
              className={bulkAction === 'released' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={bulkAction === 'rejected' ? 'destructive' : 'default'}
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : bulkAction === 'released' ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Release {selectedBatches.size} Batch(es)</>
              ) : (
                <><XCircle className="w-4 h-4 mr-2" /> Reject {selectedBatches.size} Batch(es)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Batch QC Dialog - with electronic signature */}
      <Dialog open={singleQcOpen} onOpenChange={setSingleQcOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {singleQcStatus === 'released' ? (
                <><CheckCircle2 className="w-5 h-5 text-green-600" /> Release Batch</>
              ) : singleQcStatus === 'rejected' ? (
                <><XCircle className="w-5 h-5 text-red-600" /> Reject Batch</>
              ) : (
                <><Clock className="w-5 h-5 text-amber-600" /> Put On Hold</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {singleQcBatch && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-mono font-medium">{singleQcBatch.batch_code}</p>
                <p className="text-sm text-muted-foreground">{singleQcBatch.product_name}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>QC Status</Label>
              <Select value={singleQcStatus} onValueChange={setSingleQcStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="released">✓ Released</SelectItem>
                  <SelectItem value="on_hold">⏳ On Hold</SelectItem>
                  <SelectItem value="rejected">✗ Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={singleQcNotes}
                onChange={(e) => setSingleQcNotes(e.target.value)}
                placeholder="QC notes..."
                rows={2}
              />
            </div>

            {/* CAPA fields for rejection */}
            {singleQcStatus === 'rejected' && (
              <>
                <div className="space-y-2">
                  <Label className="text-red-700">Corrective Action (Required) *</Label>
                  <Textarea
                    value={singleCorrectiveAction}
                    onChange={(e) => setSingleCorrectiveAction(e.target.value)}
                    placeholder="Describe the corrective action to be taken..."
                    rows={2}
                    className="border-red-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Corrective Action Due Date</Label>
                  <Input
                    type="text"
                    value={singleCorrectiveActionDueDate}
                    onChange={(e) => setSingleCorrectiveActionDueDate(e.target.value)}
                    placeholder="DD/MM/YYYY"
                  />
                </div>
              </>
            )}

            {/* Password confirmation */}
            <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-blue-700 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Electronic Signature (Required) *
              </Label>
              <Input
                type="password"
                value={singleQcPassword}
                onChange={(e) => setSingleQcPassword(e.target.value)}
                placeholder="Enter your password to confirm"
              />
              <p className="text-xs text-blue-600">Your password confirms this QC decision</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSingleQcOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleSingleQcUpdate} 
              disabled={processing || !singleQcPassword || (singleQcStatus === 'rejected' && !singleCorrectiveAction)}
              className={singleQcStatus === 'released' ? 'bg-green-600 hover:bg-green-700' : singleQcStatus === 'rejected' ? '' : 'bg-amber-600 hover:bg-amber-700'}
              variant={singleQcStatus === 'rejected' ? 'destructive' : 'default'}
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
              ) : (
                <>Confirm QC Status</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
